import { useLocation } from "wouter";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Users, ChevronRight, AlertCircle, CheckCircle, MinusCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { SectionHelp } from "@/components/help";

interface TeamCapacityData {
  thisWeek: string;
  nextWeek: string;
  overAllocated: number;
  underAllocated: number;
  optimal: number;
  totalTeamMembers: number;
}

interface TeamCapacityWidgetProps {
  capacity: TeamCapacityData;
}

export default function TeamCapacityWidget({ capacity }: TeamCapacityWidgetProps) {
  const [, navigate] = useLocation();

  return (
    <Card className="h-full border-0 shadow-lg bg-card">
      <CardHeader className="pb-3 border-b border-border flex flex-row items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 shadow-md">
            <Users className="h-5 w-5 text-white" />
          </div>
          <SectionHelp contentId="dashboard:team-capacity" tooltip="Learn about team capacity">
            <CardTitle className="text-lg font-bold">Team Capacity</CardTitle>
          </SectionHelp>
        </div>
        <Button 
          variant="ghost" 
          size="sm" 
          className="text-cyan-600 dark:text-cyan-400 hover:text-cyan-700 dark:hover:text-cyan-300 hover:bg-cyan-50 dark:hover:bg-cyan-950/30 gap-1 font-semibold h-8 px-2"
          onClick={() => navigate("/resources")}
        >
          Manage <ChevronRight className="h-3 w-3" />
        </Button>
      </CardHeader>
      <CardContent className="p-5">
        <div className="grid grid-cols-3 gap-3">
          <div className={cn(
            "text-center p-4 rounded-xl transition-transform hover:scale-105",
            "bg-gradient-to-br from-red-50 to-red-100/50 border border-red-100"
          )}>
            <div className="w-10 h-10 rounded-xl bg-red-100 dark:bg-red-900/30 flex items-center justify-center mx-auto mb-2">
              <AlertCircle className="h-5 w-5 text-red-500" />
            </div>
            <div className="text-2xl font-bold text-red-600 dark:text-red-400">{capacity.overAllocated}</div>
            <div className="text-xs font-medium text-red-600/80">Over</div>
          </div>
          <div className={cn(
            "text-center p-4 rounded-xl transition-transform hover:scale-105",
            "bg-gradient-to-br from-emerald-50 to-emerald-100/50 border border-emerald-100"
          )}>
            <div className="w-10 h-10 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mx-auto mb-2">
              <CheckCircle className="h-5 w-5 text-emerald-500" />
            </div>
            <div className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">{capacity.optimal}</div>
            <div className="text-xs font-medium text-emerald-600/80">Optimal</div>
          </div>
          <div className={cn(
            "text-center p-4 rounded-xl transition-transform hover:scale-105",
            "bg-gradient-to-br from-amber-50 to-amber-100/50 border border-amber-100"
          )}>
            <div className="w-10 h-10 rounded-xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center mx-auto mb-2">
              <MinusCircle className="h-5 w-5 text-amber-500" />
            </div>
            <div className="text-2xl font-bold text-amber-600 dark:text-amber-400">{capacity.underAllocated}</div>
            <div className="text-xs font-medium text-amber-600/80">Under</div>
          </div>
        </div>
        <div className="mt-4 text-center">
          <span className="text-sm text-muted-foreground">
            <span className="font-semibold text-foreground">{capacity.totalTeamMembers}</span> team members tracked
          </span>
        </div>
      </CardContent>
    </Card>
  );
}
